import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaPlus } from 'react-icons/fa';
import LabelChip from './LabelChip';
import styles from './LabelPicker.module.css';

export default function LabelPicker({
  available = [],
  selectedIds = [],
  onChange,
  onCreateLabel,
  placeholder = 'Add labels…',
  emptyHint = 'No labels yet',
  compact = false
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    function onClickAway(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, []);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedLabels = available.filter((l) => selectedSet.has(l.id));
  const filteredAvailable = useMemo(() => {
    const q = query.trim().toLowerCase();
    return available
      .filter((l) => !selectedSet.has(l.id))
      .filter((l) => !q || l.name.toLowerCase().includes(q));
  }, [available, selectedSet, query]);

  const exactMatchExists = useMemo(() => {
    const q = query.trim().toLowerCase();
    return available.some((l) => l.name.toLowerCase() === q);
  }, [available, query]);

  const toggle = (label) => {
    if (selectedSet.has(label.id)) {
      onChange(selectedIds.filter((id) => id !== label.id));
    } else {
      onChange([...selectedIds, label.id]);
    }
  };

  const remove = (label) => {
    onChange(selectedIds.filter((id) => id !== label.id));
  };

  const handleCreate = async () => {
    const name = query.trim();
    if (!name || !onCreateLabel) return;
    const created = await onCreateLabel(name);
    if (created) {
      onChange([...selectedIds, created.id]);
      setQuery('');
    }
  };

  return (
    <div className={styles.picker} ref={wrapRef}>
      <div
        className={[styles.box, compact ? styles.boxCompact : '', open ? styles.boxOpen : '']
          .filter(Boolean)
          .join(' ')}
        onClick={() => setOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        {selectedLabels.length === 0 && !open ? (
          <span className={styles.placeholder}>{placeholder}</span>
        ) : null}
        {selectedLabels.map((l) => (
          <LabelChip
            key={l.id}
            label={l.name}
            color={l.color}
            onRemove={(e) => {
              e.stopPropagation();
              remove(l);
            }}
          />
        ))}
        {open ? (
          <input
            autoFocus
            className={styles.search}
            placeholder="Search or create…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && query.trim() && !exactMatchExists) {
                e.preventDefault();
                handleCreate();
              }
            }}
          />
        ) : null}
      </div>

      {open ? (
        <div className={styles.menu} role="listbox">
          {filteredAvailable.length === 0 ? (
            <div className={styles.empty}>
              {available.length === 0 ? emptyHint : 'No matches'}
            </div>
          ) : (
            <ul>
              {filteredAvailable.map((l) => (
                <li key={l.id}>
                  <button
                    type="button"
                    className={styles.option}
                    onClick={() => toggle(l)}
                  >
                    <LabelChip label={l.name} color={l.color} />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {onCreateLabel && query.trim() && !exactMatchExists ? (
            <button type="button" className={styles.createBtn} onClick={handleCreate}>
              <FaPlus /> Create "{query.trim()}"
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
